import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import {
  getBatchSearchImpactSummary,
  getProductSearchImpact,
  refreshProductSearchImpact,
  setReviewItemDismissed,
} from '~/features/catalog-operations/services/search-impact.service';
import type { ProductSearchImpact } from '~/features/catalog-operations/types/search-impact.types';
import { extractApiError } from '~/utils/extract-api-error';

/** Search Impact for one product, pinned to the batch being viewed (results are per producer). */
export function useProductSearchImpact(
  productId: string,
  enrichmentJobId?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.productSearchImpact(productId, enrichmentJobId),
    queryFn: () => getProductSearchImpact(productId, enrichmentJobId),
    enabled,
  });
}

/** Recomputes one product and writes the fresh payload straight into the cache (no refetch needed). */
export function useRefreshProductSearchImpact(productId: string, enrichmentJobId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => refreshProductSearchImpact(productId, enrichmentJobId),
    onSuccess: (fresh) => {
      queryClient.setQueryData(
        queryKeys.catalogOperations.productSearchImpact(productId, enrichmentJobId),
        fresh,
      );
    },
    onError: (err) => {
      toast.error(extractApiError(err, "Couldn't recalculate search impact."));
    },
  });
}

/** Dismisses or restores a review item, optimistically, rolling back if the server refuses. */
export function useSetReviewItemDismissed(productId: string, enrichmentJobId?: string) {
  const queryClient = useQueryClient();
  const key = queryKeys.catalogOperations.productSearchImpact(productId, enrichmentJobId);
  return useMutation({
    mutationFn: ({ itemKey, dismissed }: { itemKey: string; dismissed: boolean }) =>
      setReviewItemDismissed(productId, itemKey, dismissed, enrichmentJobId),
    onMutate: async ({ itemKey, dismissed }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProductSearchImpact>(key);
      if (previous) {
        queryClient.setQueryData<ProductSearchImpact>(key, {
          ...previous,
          review: previous.review.map((item) =>
            item.itemKey === itemKey ? { ...item, dismissed } : item,
          ),
        });
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      toast.error(extractApiError(err, "Couldn't update the review item."));
    },
  });
}

/** Batch strip figures for the job being viewed; null until the batch has been computed. */
export function useBatchSearchImpactSummary(jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.jobSearchImpactSummary(jobId ?? ''),
    queryFn: () => getBatchSearchImpactSummary(jobId ?? ''),
    enabled: enabled && !!jobId,
  });
}
